import { useTranslation } from "react-i18next";
import { useRecoilCallback } from "recoil";
import { completeUserSim, UserSimStreamRequest } from "api/userSimApi";
import { ProgramContentType } from "data/BlockComponent";
import { isUserSimCompleteTask } from "logic/autoFill";
import { getTaskIdFromTaskKey } from "logic/logics";
import { buildUserSimRequest, withSentFeedback } from "logic/userSimRequest";
import { getLatestTranslationVersion } from "hooks/useGetBlankTaskData";
import { userAtom } from "recoil/userAtom";
import { patientAtom } from "recoil/patientAtom";
import { taskDataAtom } from "recoil/taskDataAtom";
import { captureException } from "utils/sentry";

const RETRY_DELAY_MS = 1000;

// 실패하면 한 번만 다시 보낸다 (Agent 는 같은 task.key 를 덮어쓴다)
const completeWithRetry = (req: UserSimStreamRequest) =>
  completeUserSim(req).catch(() =>
    new Promise<void>((resolve) => setTimeout(resolve, RETRY_DELAY_MS)).then(
      () => completeUserSim(req)
    )
  );

// 교육용 모의 내담자가 마치기 알림 대상 활동지를 마치면 최종 값을 user_sim Agent 에 알린다.
// 보낼 때만 Promise 를 돌려준다 (실패해도 resolve). 그 밖의 사용자는 undefined.
// 상태는 호출 시점에 snapshot 으로 읽는다 — 모든 마치기 버튼이 atom 을 구독하지 않도록.
export default function useUserSimComplete({
  taskKey,
  title,
  translationVersion,
  thoughtRecordKey,
}: {
  taskKey: string;
  title: string;
  translationVersion?: string;
  thoughtRecordKey?: string;
}) {
  const taskId = getTaskIdFromTaskKey(taskKey);
  // 헤더의 마치기 버튼은 translationVersion 을 받지 않는다 — 최신 버전으로 대신
  const version = translationVersion ?? getLatestTranslationVersion(taskId);
  const { t } = useTranslation("translation", {
    keyPrefix: `task.activity.${taskId}.${version}`,
  });
  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });

  return useRecoilCallback(
    ({ snapshot, set }) =>
      (content?: ProgramContentType[]): Promise<void> | undefined => {
        const user = snapshot.getLoadable(userAtom).getValue();
        if (!user?.isSimulated || !content || !isUserSimCompleteTask(taskKey)) {
          return undefined;
        }
        return buildUserSimRequest({
          taskKey,
          title,
          thoughtRecordKey,
          content,
          t,
          t_common,
          accessToken: user.accessToken || "",
          pid: snapshot.getLoadable(patientAtom).getValue()?.patientId,
          taskDataState: snapshot.getLoadable(taskDataAtom).getValue(),
          setTaskDataState: (value) => set(taskDataAtom, value),
        })
          .then((req) => completeWithRetry(withSentFeedback(req, content)))
          .catch((error) =>
            captureException(error, {
              tags: { action: "user_sim_complete" },
              extra: { taskKey },
            })
          );
      },
    [taskKey, title, thoughtRecordKey, t, t_common]
  );
}
